import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { environment } from '../environments/environment';
import { Subscription } from 'rxjs';
import {
  LogoConfig,
  NavigationItem,
  NavigationService,
  ToastComponent,
} from '@angular-monorepo/ui';
import { DashboardService } from '@angular-monorepo/dashboard';
import { MonitoringService } from '@angular-monorepo/monitoring';
import { UsersService } from '@angular-monorepo/users';
import { TranslateService } from '@ngx-translate/core';
import { AuthConfigService } from '@angular-monorepo/auth';
import { ConfigService } from '../environments/environment.service';

@Component({
  imports: [RouterModule, ToastComponent],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'mareauto';
  environment = environment;
  private readonly navigationService = inject(NavigationService);
  private readonly monitoringService = inject(MonitoringService);
  private readonly usersService = inject(UsersService);
  private readonly configService = inject(ConfigService);
  private readonly translate = inject(TranslateService);
  private readonly authConfigService = inject(AuthConfigService);
  private readonly dashboardService = inject(DashboardService);
  private langChangeSub!: Subscription;

  constructor() {
    this.monitoringService.configure({
      apiUrl: this.configService.getApiUrl(),
    });

    this.dashboardService.configure({
      apiUrl: this.configService.getApiUrl(),
    });

    this.usersService.configure({
      apiUrl: this.configService.getApiUrl(),
    });

    this.setupAuthConfig();
  }

  ngOnInit() {
    this.setupNavigation();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.setupNavigation();
      this.setupUserNavigation();
    });

    this.setupUserNavigation();
    this.updateLogo();
    this.updateBackground();
    this.setupFooter();
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }

  private setupAuthConfig(): void {
    this.authConfigService.setConfig({
      apiUrl: this.configService.getApiUrl(),
      enableOwnRolesRequest: false,
      postLoginRoute: '/dashboard',
    });
  }

  private setupNavigation(): void {
    this.translate
      .get(['sidebar.dashboard', 'sidebar.monitoring', 'sidebar.users'])
      .subscribe((translations) => {
        const navigationItems: NavigationItem[] = [
          {
            name: translations['sidebar.dashboard'],
            href: '/dashboard',
            icon: 'pi pi-home',
            current: false,
          },
          {
            name: translations['sidebar.monitoring'],
            href: '/monitoreo',
            icon: 'pi pi-chart-line',
            current: false,
          },
          {
            name: translations['sidebar.users'],
            href: '/usuarios',
            icon: 'pi pi-users',
            current: false,
          },
        ];

        this.navigationService.updateNavigation(navigationItems);
      });
  }

  private setupUserNavigation(): void {
    const logoutLabel = this.translate.instant('common.logout');

    this.navigationService.updateUserNavigation([
      {
        label: logoutLabel,
        icon: 'pi pi-sign-out',
        command: () => this.signOut(),
      },
    ]);
  }

  private signOut(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('refresh_token');

    globalThis.location.href = '/login';
  }

  private updateLogo(): void {
    const logoUrl: LogoConfig = {
      type: 'image',
      src: 'assets/images/mareauto.png',
      alt: 'mareauto',
      class: 'h-20',
    };
    this.navigationService.updateLogo(logoUrl);
  }

  private updateBackground(): void {
    this.navigationService.updateBackground('assets/images/background.jpg');
  }

  private setupFooter(): void {
    this.navigationService.updateFooterConfig({
      showDefault: false,
      appName: this.title,
      environment: this.configService.getEnvironment(),
      hideForgotPasswordLink: true,
      hideRegisterLink: true,
      customElements: [
        {
          type: 'component',
          content: 'lib-theme-selector',
          props: { variant: 'toggle' },
        },
      ],
    });
  }
}
